import React from "react";
import { Smartphone, Sparkles } from "@/lib/icons";

/**
 * Grand "Coming soon on the App Store and Play Store" section — sits
 * directly below the hero. Purely marketing: buttons are non-clickable
 * for now (the apps are in the works).
 */
export default function ComingSoonApps() {
  return (
    <section className="relative container-wide py-14 md:py-20" data-testid="apps-coming-soon">
      {/* restrained brand glow behind the section */}
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[380px] w-[760px] max-w-full rounded-full bg-violet-600/10 blur-3xl" />
      </div>

      <div className="relative glass-strong rounded-[32px] noise overflow-hidden">
        {/* quiet brand border */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[32px]"
          style={{
            background: "rgba(157,0,255,0.3)",
            padding: "1px",
            WebkitMask:
              "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
          }}
        />
        <div className="relative p-8 md:p-14 grid md:grid-cols-12 gap-10 items-center">
          {/* Left copy */}
          <div className="md:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1 text-[10px] uppercase tracking-widest text-fuchsia-300 border border-fuchsia-400/25">
              <Sparkles size={11} className="text-fuchsia-300" /> Coming Soon
            </div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-white">
              PRONET <span className="text-gradient">on the go</span>
            </h2>
            <p className="mt-4 text-sm md:text-base text-white/60 max-w-lg">
              Search professionals, manage your profile and connect faster from your phone.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <StoreBadge kind="apple" />
              <StoreBadge kind="google" />
            </div>
            <div className="mt-4 flex items-center gap-2 text-[11px] text-white/40">
              <Smartphone size={12} className="text-fuchsia-300/70" />
              Coming to <span className="text-white/70">iOS &amp; Android</span>.
            </div>
          </div>

          {/* Right glowing phone-mockup */}
          <div className="md:col-span-5 relative fade-up-slow">
            <PhoneMockup />
          </div>
        </div>
      </div>
    </section>
  );
}

function StoreBadge({ kind }) {
  const isApple = kind === "apple";
  return (
    <button
      type="button"
      disabled
      data-testid={isApple ? "app-store-badge" : "play-store-badge"}
      title="Coming soon"
      aria-label={isApple ? "Download on the App Store, coming soon" : "Get it on Google Play, coming soon"}
      className="group relative inline-flex items-center gap-3 rounded-2xl border border-white/15 bg-white/[0.03] hover:bg-white/[0.06] transition-all duration-500 px-4 md:px-5 py-3 md:py-3.5 min-w-[190px] cursor-not-allowed"
    >
      {/* hover-only violet glow */}
      <span className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ boxShadow: "0 10px 36px -10px rgba(127,0,255,0.6)" }} />
      {/* icon */}
      <span className="shrink-0 text-white">
        {isApple ? <AppleGlyph /> : <PlayGlyph />}
      </span>
      <span className="flex flex-col leading-tight text-left">
        <span className="text-[10px] uppercase tracking-widest text-white/55">
          {isApple ? "Download on the" : "Get it on"}
        </span>
        <span className="font-semibold text-white text-base md:text-lg tracking-tight">
          {isApple ? "App Store" : "Google Play"}
        </span>
      </span>
      <span className="absolute -top-2 right-3 rounded-full bg-fuchsia-500/80 text-white text-[9px] font-semibold uppercase tracking-widest px-2 py-0.5 shadow-[0_4px_16px_rgba(127,0,255,0.6)]">
        Soon
      </span>
    </button>
  );
}

function AppleGlyph() {
  // Simplified Apple silhouette (public-domain path)
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.36-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.36C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function PlayGlyph() {
  // Full-colour Google Play triangle (four triangular gradients)
  return (
    <svg width="26" height="30" viewBox="0 0 128 128" aria-hidden>
      <defs>
        <linearGradient id="pg-a" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#00d3ff" />
          <stop offset="1" stopColor="#0091ea" />
        </linearGradient>
        <linearGradient id="pg-b" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#7c4dff" />
          <stop offset="1" stopColor="#c724b1" />
        </linearGradient>
        <linearGradient id="pg-c" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffe57f" />
          <stop offset="1" stopColor="#ffab00" />
        </linearGradient>
        <linearGradient id="pg-d" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#ff6d3c" />
          <stop offset="1" stopColor="#e53935" />
        </linearGradient>
      </defs>
      <path fill="url(#pg-b)" d="M12 4l68 60L12 124V4z" />
      <path fill="url(#pg-a)" d="M12 4l68 60-14.5 13-56-73z" />
      <path fill="url(#pg-c)" d="M80 64l28-16-28-16v32z" />
      <path fill="url(#pg-d)" d="M80 64l28 16-28 16V64z" />
    </svg>
  );
}

function PhoneMockup() {
  return (
    <div className="relative mx-auto w-[220px] h-[430px] md:w-[240px] md:h-[470px]">
      {/* restrained outer brand glow */}
      <div className="absolute -inset-8 rounded-[60px] bg-violet-600/20 blur-3xl" />
      {/* phone frame */}
      <div className="relative w-full h-full rounded-[44px] bg-ink-950 border border-violet-500/20 shadow-[0_24px_70px_-20px_rgba(127,0,255,0.55)] overflow-hidden">
        {/* notch */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-24 rounded-full bg-black/80 border border-white/5" />
        {/* screen content — mini fake home */}
        <div className="absolute inset-2 rounded-[36px] bg-ink-900 overflow-hidden">
          <div className="p-4 pt-8">
            <div className="h-2 w-16 rounded bg-white/20" />
            <div className="mt-2 h-3 w-32 rounded bg-white/30" />
            <div className="mt-6 grid grid-cols-3 gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-square rounded-lg bg-violet-600/35 border border-violet-500/20" />
              ))}
            </div>
            <div className="mt-5 h-16 rounded-xl bg-white/[0.06] border border-white/10" />
            <div className="mt-3 h-24 rounded-xl bg-white/[0.06] border border-white/10" />
          </div>
          {/* subtle scan-line shimmer */}
          <div className="absolute inset-0 pointer-events-none opacity-40 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.15),transparent_50%)]" />
        </div>
        {/* speaker */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 h-1 w-20 rounded-full bg-white/10" />
      </div>
    </div>
  );
}
